import { createI18n as createVueI18n, DatetimeFormat, NumberFormat } from 'vue-i18n'
import type { I18n, VueI18n, VueI18nOptions } from 'vue-i18n'
import { bsConstants } from 'booksprout'
import I18nT from '../components/I18nT'

/**
 * v9's `LocaleMessages` takes type parameters describing the message SCHEMA. Ours are assembled at
 * build time by three `require.context` calls, so there is no schema to give it: a locale key mapping
 * to loaded JSON is the whole of what we know.
 */
type Messages = Record<string, any>
type LocaleFile = Record<string, any>
type EagerContext = ((key: string) => LocaleFile) & { keys: () => string[] }
type LazyContext = ((key: string) => Promise<LocaleFile>) & { keys: () => string[] }

export type LocaleSources = {
  cookieLocale?: string | null
  userLocale?: string | null
  acceptLanguage?: string | null
}

const DEFAULT_LOCALE = bsConstants.I18N.DEFAULT_LOCALE
const SUPPORTED_LOCALES = bsConstants.I18N.LOCALES.map(locale => locale.CODE)

const merge = (target: any, source: any) => {
  // Iterate through `source` properties and if an `Object` set property to merge of `target` and `source` properties
  for (const key of Object.keys(source)) {
    if (source[key] instanceof Object && key in target) Object.assign(source[key], merge(target[key], source[key]))
  }

  // Join `target` and modified `source`
  Object.assign(target || {}, source)
  return target
}

function mergeKeys (messages: Messages, locales: EagerContext) {
  locales.keys().forEach((key: string) => {
    const matched = key.match(/([A-Za-z0-9-_]+)\./i)
    if (matched && matched.length > 1) {
      const locale = matched[1]
      if (messages[locale] === void 0) {
        // locale doesn't exist on our messages i.e en-US. Add it.
        messages[locale] = locales(key)
      } else {
        // locale exists. Merge with the new one (this will deep merge so we can override one property).
        merge(messages[locale], locales(key))
      }
    }
  })
}

/**
 * The three locale sources, in precedence order - each one deep merges over the last, so an app
 * can override a common-app string and common-app can override a shared one.
 *
 * Every argument has to be written out in full here. Webpack evaluates require.context while
 * parsing, so a path or pattern held in a variable - even a `const` two lines up - stops it
 * recognising the call at all: it degrades to a dynamic require, warns "Critical dependency:
 * require function is used in a way in which dependencies cannot be statically extracted", and
 * the app fails to boot. Hence the repetition below.
 *
 * The default locale is bundled with the app because it doubles as the fallback; every other
 * locale is a lazy chunk, so a reader who never switches language never pays for the five they
 * didn't pick.
 */
function defaultLocaleContexts (): EagerContext[] {
  return [
    // Translations shared by the API and apps
    // @ts-expect-error webpack injects require.context; it is absent from the Require type
    require.context('../../../../../../shared/libs/common/src/i18n/locales', true, /en-US\.json$/),
    // Base translations which are used for each app (system ones mainly)
    // @ts-expect-error webpack injects require.context; it is absent from the Require type
    require.context('../../../common-app/src/i18n/locales', true, /en-US\.json$/),
    // Specific app translations
    // @ts-expect-error webpack injects require.context; it is absent from the Require type
    require.context('src/i18n/locales', true, /en-US\.json$/)
  ]
}

function otherLocaleContexts (): LazyContext[] {
  return [
    // @ts-expect-error webpack injects require.context; it is absent from the Require type
    require.context('../../../../../../shared/libs/common/src/i18n/locales', true, /^\.\/(?!en-US)[A-Za-z0-9-_,\s]+\.json$/i, 'lazy'),
    // @ts-expect-error webpack injects require.context; it is absent from the Require type
    require.context('../../../common-app/src/i18n/locales', true, /^\.\/(?!en-US)[A-Za-z0-9-_,\s]+\.json$/i, 'lazy'),
    // @ts-expect-error webpack injects require.context; it is absent from the Require type
    require.context('src/i18n/locales', true, /^\.\/(?!en-US)[A-Za-z0-9-_,\s]+\.json$/i, 'lazy')
  ]
}

function loadDefaultLocaleMessages (): Messages {
  const messages: Messages = {}
  for (const context of defaultLocaleContexts()) {
    mergeKeys(messages, context)
  }
  return messages
}

/**
 * Pull one locale's chunks off the network (or out of the SSR bundle) and merge the three
 * sources into a single message object. A source that has no file for the locale is skipped,
 * so a partially translated locale still works - anything missing falls back to en-US.
 */
async function loadLocaleMessages (locale: string): Promise<LocaleFile> {
  const fileName = `./${locale}.json`
  const messages: LocaleFile = {}

  for (const context of otherLocaleContexts()) {
    if (!context.keys().includes(fileName)) {
      continue
    }
    merge(messages, await context(fileName))
  }

  return messages
}

const isSupportedLocale = (locale?: string | null): boolean =>
  locale !== null && locale !== void 0 && SUPPORTED_LOCALES.includes(locale)

/**
 * Map anything locale-shaped onto one of ours. An exact code wins; failing that we match on the
 * language subtag alone, so a reader whose browser asks for `es`, `es-MX` or `es-419` still lands
 * on Spanish, and `pt-PT` lands on our Brazilian Portuguese rather than falling back to English.
 */
const normaliseLocale = (locale?: string | null): string | undefined => {
  if (locale === null || locale === void 0 || locale === '') {
    return void 0
  }

  const wanted = locale.trim()
  const exact = SUPPORTED_LOCALES.find(supported => supported.toLowerCase() === wanted.toLowerCase())
  if (exact !== void 0) {
    return exact
  }

  const language = wanted.split('-')[0].toLowerCase()
  return SUPPORTED_LOCALES.find(supported => supported.split('-')[0].toLowerCase() === language)
}

/**
 * `en-GB,en;q=0.9,es;q=0.8` -> ['en-GB', 'en', 'es'], most wanted first.
 */
const parseAcceptLanguage = (header?: string | null): string[] => {
  if (header === null || header === void 0 || header === '') {
    return []
  }

  return header
    .split(',')
    .map(part => {
      const [tag, ...params] = part.trim().split(';')
      const quality = params
        .map(param => param.trim().match(/^q=([0-9.]+)$/))
        .find(match => match !== null)

      return { tag: tag.trim(), quality: quality ? Number(quality[1]) : 1 }
    })
    .filter(entry => entry.tag !== '' && !Number.isNaN(entry.quality))
    .sort((a, b) => b.quality - a.quality)
    .map(entry => entry.tag)
}

/**
 * An explicit choice always wins over a guess: the cookie is what the reader last picked on this
 * device, the preference is what they picked on any device, and Accept-Language is only a
 * first-visit hint. Anything unrecognised drops through to en-US.
 */
const resolveLocale = ({ cookieLocale, userLocale, acceptLanguage }: LocaleSources): string => {
  const candidates = [cookieLocale, userLocale, ...parseAcceptLanguage(acceptLanguage)]

  for (const candidate of candidates) {
    const normalised = normaliseLocale(candidate)
    if (normalised !== void 0) {
      return normalised
    }
  }

  return DEFAULT_LOCALE
}

/**
 * Typed as the LEGACY options interface plus the two flags that select it. `I18nOptions` is a union of
 * the legacy and composition shapes, and TypeScript cannot narrow it from a `legacy: true` inside the
 * same literal -- so `silentTranslationWarn`, which only exists on the legacy side, reads as an error
 * against the union.
 */
type LegacyOptions = VueI18nOptions & { legacy: true, globalInjection: boolean }

/**
 * One instance per request. The SSR bundle runs with `runInNewContext: false`, so anything held at
 * module scope is shared by every reader being rendered at that moment - a single shared instance
 * would mean one reader switching to Spanish could render Spanish for someone else mid-request.
 *
 * What comes back is the APP-LEVEL instance, which is what `app.use()` takes; the translator the
 * components see is its `.global` (see i18nModule's `init`). v8's `new VueI18n(...)` was both.
 */
const createI18n = (): I18n => {
  const options: LegacyOptions = {
    /**
     * Legacy mode, deliberately: it keeps `$t`/`$tc` on the component instance and the `VueI18n`
     * instance API that `i18nModule` and every template already use. Moving to the composition API
     * (`legacy: false`, `useI18n()` per component) is a separate job from the framework bump, and
     * doing both at once would make every template change untestable against the old behaviour.
     */
    legacy: true,
    // Vue 3 does not put `$t` on the instance by itself the way `Vue.use(VueI18n)` did.
    globalInjection: true,
    locale: DEFAULT_LOCALE,
    fallbackLocale: DEFAULT_LOCALE,
    messages: loadDefaultLocaleMessages(),
    silentTranslationWarn: true
  }

  return createVueI18n(options)
}

/**
 * Switch an instance over to `locale`, fetching its messages the first time it is asked for.
 * Returns the locale actually applied, which is en-US if the requested one isn't one of ours.
 *
 * Takes the translator (`i18n.global`, or a component's `$i18n`) rather than the app-level
 * instance: v9 puts `locale` and `setLocaleMessage` there, and the language picker only ever has
 * `$i18n` to hand.
 */
const applyLocale = async (i18n: VueI18n, locale?: string | null): Promise<string> => {
  const target = normaliseLocale(locale) ?? DEFAULT_LOCALE

  if (target !== DEFAULT_LOCALE && i18n.messages[target] === void 0) {
    i18n.setLocaleMessage(target, await loadLocaleMessages(target))
  }

  i18n.locale = target
  return target
}

const configure = (i18n: any, app: any) => {
  /**
   * Helper function for i18n. By default, if a key isn't found, the key is returned.
   * Sometimes though, we just want an empty string (or something else) as a default,
   * this function provides a way for that to happen.
   * @param key
   * @param defaultString
   */
  app.config.globalProperties.$tcD = (key: string, _defaultString: string) => {
    const result = i18n.global.tc(key)
    if (key === result) {
      return ''
    }
    return result
  }

  /**
   * The i18n components, registered here rather than by vue-i18n: each app's boot file installs the
   * instance with `globalInstall: false`, so that `i18n-t` can be our `I18nT`, which renders a span by
   * default as Vue 2's `<i18n>` did. `i18n-n` and `i18n-d` are vue-i18n's own, under the names it
   * would have given them. `$t` and the `v-t` directive are unaffected by the flag.
   */
  ;[ 'i18n-t', 'I18nT' ].forEach(name => app.component(name, I18nT))
  ;[ 'i18n-n', 'I18nN' ].forEach(name => app.component(name, NumberFormat))
  ;[ 'i18n-d', 'I18nD' ].forEach(name => app.component(name, DatetimeFormat))

  // `app.i18n = i18n` used to be how the instance was reached. Vue 3 installs it as a plugin
  // instead (`app.use(i18n)`, in each app's boot file) and nothing reads `app.i18n` any more --
  // the boot file reads the request's instance back through `useI18n()`.
}

export {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  configure,
  createI18n,
  applyLocale,
  resolveLocale,
  normaliseLocale,
  parseAcceptLanguage,
  isSupportedLocale
}
